import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ELECTRICAL_FUNCTIONS } from './electrical-calibration-profile';
import {
  certificateImageAsDataUrl,
  drawCertificateFooter,
  drawCertificateHeader,
  drawCertificateLegalText,
  drawReferenceStandardsTable,
} from './certificate-pdf-layout';

/**
 * Certificado ELÉCTRICO (multímetro/amperímetro/ohmímetro) NO SAC.
 * Basado en el generador de presión (mismo encabezado CALINS, firma, texto
 * legal y numeración), adaptado al formato de 4 páginas con tablas por función.
 */

const LOGO_URL = '/logo-calins-color-hd.png';
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

// ── Helpers (compartidos con el de presión) ────────────────────────────────
function asNumber(v) { const x = Number(v); return Number.isFinite(x) ? x : null; }
function fmtNumber(v, decimals = 3) { const x = asNumber(v); return x === null ? '-' : x.toFixed(decimals).replace('.', ','); }
function fmtPct(v, decimals = 2) { const x = asNumber(v); return x === null ? '-' : `${x.toFixed(decimals).replace('.', ',')}%`; }
// Valor con decimales adaptativos (para que 10.000.000 Ω no se vea horrible).
function fmtVal(v) {
  const x = asNumber(v);
  if (x === null) return '-';
  const a = Math.abs(x);
  const dec = a >= 1000 ? 0 : a >= 10 ? 2 : 3;
  return x.toFixed(dec).replace('.', ',');
}
function dmy(value) {
  if (!value) return '-';
  // Fecha sin hora ("2027-01-21") se parsea como medianoche UTC y en hora local
  // (UTC-3) retrocedía un día. Se ancla al mediodía local.
  const s = String(value);
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T12:00:00` : value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}
function chunk(arr, n) { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; }

async function fetchImageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}

function sanitizeObservations(text) {
  if (!text) return '';
  const banned = [
    'FUNCIONA, PERO NO ESTÁ DENTRO DE LA BANDA DECLARADA POR EL FABRICANTE',
    'FUNCIONA, PERO NO ESTA DENTRO DE LA BANDA DECLARADA POR EL FABRICANTE',
  ];
  let t = String(text);
  banned.forEach((b) => { t = t.split(b).join(''); });
  return t.replace(/\s+/g, ' ').trim();
}

function resolveCertificateNumber(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (/^\d+$/.test(text)) return text;
    const m = text.match(/OS-(\d+)-(\d+)$/i);
    if (m) return `${m[1]}${m[2]}`;
  }
  return '-';
}

function drawFooterTemplate(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}

function drawHeaderTemplate(doc, { logoDataUrl, certificateNumber, page, pages, variant }) {
  drawCertificateHeader(doc, {
    logo: logoDataUrl,
    number: certificateNumber,
    page,
    pages,
    first: variant === 'page1',
  });
}

function drawLegalParagraph(doc, x, y, maxWidth) {
  return drawCertificateLegalText(doc, y, { x, width: maxWidth, fontSize: 10 });
}

// ── PÁGINA 1: especificaciones + cliente + condiciones ──────────────────────
function drawPage1(doc, { session, instrument, determinations }) {
  const w = doc.internal.pageSize.getWidth();
  let y = 66;
  y = drawLegalParagraph(doc, 18, y, w - 36) + 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('ESPECIFICACIONES', 14, y);
  y += 7;

  const colL = 14, colV = 70, colR = 120, colRV = w - 14, rowH = 5.2;
  const leftRows = [
    { k: 'Objeto:', v: session.instrument_object || instrument?.type || 'Multímetro' },
    { k: 'Fabricante:', v: session.instrument_brand || instrument?.brand || '-' },
    { k: 'Modelo:', v: session.instrument_model || instrument?.model || '-' },
    { k: 'Número de Serie:', v: session.instrument_serial || instrument?.serial || '-' },
    { k: 'TAG:', v: session.instrument_tag || instrument?.tag || '-' },
    { k: 'Fecha de Calibración:', v: dmy(session.calibration_date) },
  ];
  const rightRows = [
    { k: 'Determinaciones requeridas:', v: 'Calibración' },
    { k: 'Clase / tolerancia:', v: session.instrument_class ? `${String(session.instrument_class).replace('.', ',')}  [%]` : '-' },
    { k: 'Fecha de emisión:', v: dmy(session.report_issue_date || new Date().toISOString()) },
    { k: 'Validez:', v: dmy(session.valid_until) },
    { k: 'Nº de mediciones:', v: String(determinations || '-') },
  ];

  const startY = y;
  let yy = startY;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  leftRows.forEach((r) => { doc.text(r.k, colL, yy); yy += rowH; });
  yy = startY;
  rightRows.forEach((r) => { doc.text(r.k, colR, yy); yy += rowH; });
  yy = startY;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  leftRows.forEach((r) => { doc.text(String(r.v ?? '-'), colV, yy); yy += rowH; });
  yy = startY;
  rightRows.forEach((r) => { doc.text(String(r.v ?? '-'), colRV, yy, { align: 'right' }); yy += rowH; });

  // CLIENTE
  y = startY + rowH * Math.max(leftRows.length, rightRows.length) + 10;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text('CLIENTE', 14, y);
  y += 9;
  const clientRows = [
    { k: 'Razón Social:', v: session.client_name || '-' },
    { k: 'Dirección:', v: session.client_address || '-' },
    { k: 'Ciudad/Dpto.:', v: session.client_city || '-' },
    { k: 'Provincia:', v: session.client_province || '-' },
  ];
  doc.setFontSize(10);
  yy = y;
  doc.setFont('helvetica', 'bold');
  clientRows.forEach((r) => { doc.text(r.k, 46, yy); yy += rowH; });
  yy = y;
  clientRows.forEach((r, i) => { doc.setFont('helvetica', i === 0 ? 'bolditalic' : 'normal'); doc.text(String(r.v ?? '-'), 92, yy); yy += rowH; });

  // CONDICIONES AMBIENTALES
  y = y + rowH * clientRows.length + 12;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text('CONDICIONES AMBIENTALES', 14, y);
  y += 10;
  const envRows = [
    { k: 'Presión atmosférica:', v: fmtNumber(session.ambient_pressure_avg ?? 0, 1), u: '[hPa] ± 1 [hPa]' },
    { k: 'Temperatura Ambiente:', v: fmtNumber(session.ambient_temp_avg ?? 0, 1), u: '[ºC] ± 1 [ºC]' },
    { k: 'Humedad Relativa:', v: fmtNumber(session.ambient_humidity_avg ?? 0, 1), u: '[% H.R.] ± 3 [% H.R.]' },
  ];
  doc.setFontSize(10);
  yy = y;
  envRows.forEach((r) => {
    doc.setFont('helvetica', 'bold'); doc.text(r.k, 74, yy, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.text(String(r.v ?? '-'), 128, yy, { align: 'right' });
    doc.text(r.u, 156, yy);
    yy += rowH;
  });

  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  const bottom = [
    'Los resultados contenidos en el presente certificado se refieren exclusivamente a los equipos ó instrumentos sometidos a',
    'calibración ó medición, así como al momento y condiciones en que se realizaron las mediciones.',
  ];
  let by = 265;
  bottom.forEach((t) => { doc.text(t, 14, by); by += 4.6; });
}

// ── Tabla combinada por grupo de funciones ──────────────────────────────────
// Cada función muestra, una al lado de la otra, sus 4 columnas
// (Patrón · Instrumento · Desviación · Incert. U). Antes había dos secciones
// separadas (lecturas y desviación) que dejaban tablas huérfanas y huecos.
function drawCombinedTable(doc, group, y, TOP, BOTTOM) {
  const maxPoints = Math.max(1, ...group.map((f) => f.maxPoint));
  const estH = (maxPoints + 3) * 6.2 + 6;
  if (y + estH > BOTTOM) { doc.addPage(); y = TOP; }

  const head = [
    [{ content: 'Punto', rowSpan: 2 }, ...group.map((f) => ({ content: f.label, colSpan: 4 }))],
    group.flatMap((f) => [
      `Patrón\n${f.unitLabel}`,
      `Instrum.\n${f.unitLabel}`,
      `Desviac.\n${f.unitLabel}`,
      `Incert. U\n${f.unitLabel}`,
    ]),
  ];
  const body = [];
  for (let p = 1; p <= maxPoints; p++) {
    const row = [String(p)];
    for (const f of group) {
      const r = f.byPoint[p];
      row.push(fmtVal(r?.pattern_mean), fmtVal(r?.instrument_mean), fmtVal(r?.error_abs), fmtVal(r?.u_reported));
    }
    body.push(row);
  }
  // Repartir el ancho útil (182 mm) para que la tabla ocupe SIEMPRE todo el ancho,
  // sin importar cuántas funciones tenga el grupo (así no queda angosta ni apretada).
  const dataCols = group.length * 4;
  const dataW = (182 - 12) / dataCols;
  const columnStyles = { 0: { cellWidth: 12 } };
  for (let i = 1; i <= dataCols; i++) columnStyles[i] = { cellWidth: dataW };
  autoTable(doc, {
    startY: y,
    margin: { left: 14, right: 14 },
    styles: { fontSize: 8, halign: 'center', valign: 'middle', lineWidth: 0.1, lineColor: [0, 0, 0], cellPadding: 1.2, overflow: 'linebreak' },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 7 },
    columnStyles,
    theme: 'grid',
    head,
    body,
  });
  return (doc.lastAutoTable?.finalY ?? y) + 8;
}

function drawResultsSections(doc, { functions }) {
  const TOP = 34, BOTTOM = 256;
  // Tamaño de grupo dinámico: 2 funciones por tabla (llena mejor la página) para
  // certificados de hasta 6 funciones; 3 por tabla para 7-8 (así nunca pasa de 3
  // tablas y entran todas en una sola página — sin hoja huérfana).
  const per = functions.length <= 6 ? 2 : 3;
  const groups = chunk(functions, per);

  doc.addPage();
  let y = TOP;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
  doc.text('RESULTADOS OBTENIDOS DURANTE LA CALIBRACIÓN', 14, y);
  y += 8;
  for (const g of groups) y = drawCombinedTable(doc, g, y, TOP, BOTTOM);
}

// ── PÁGINA FINAL: observaciones + método + patrón + firma ───────────────────
function drawFinalPage(doc, { session, signatureDataUrl }) {
  doc.addPage();
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text('OBSERVACIONES', 14, 45);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5);
  doc.text(
    'El valor de la incertidumbre expandida fue calculado multiplicando la incertidumbre estándar\n' +
    'combinada por el factor de cobertura k=2 correspondiente a un nivel de confianza cercano al 95%.',
    14, 58
  );
  const extraObs = sanitizeObservations(session.notes || session.observations);
  if (extraObs) doc.text(extraObs, 14, 74, { maxWidth: 186 });

  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text('MÉTODO Y PROCEDIMIENTO', 14, 98);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5);
  doc.text('Generación de señal y medición por comparación mediante instrumento patrón.', 14, 106);

  const patternTableEnd = drawReferenceStandardsTable(doc, {
    startY: 122,
    references: [{
      usage: 'Referencia',
      description: session.standard_name || 'Clamp Multimeter Calibrator',
      serial: session.standard_serial,
      certificateNo: session.standard_certificate,
      calibrationDate: session.standard_calibration_date,
      validUntil: session.standard_valid_until || session.standard_due_date,
      scope: 'Funciones eléctricas configuradas',
    }],
  });

  let dy = patternTableEnd + 9;
  const defs = [
    ['Posición del instrumento calibrado', sanitizeObservations(session.instrument_position || 'Posición Normal.')],
    ['Error del instrumento', 'Es el valor obtenido de la diferencia entre la indicación del instrumento y la indicada por el\npatrón para un mismo punto considerado.'],
  ];
  for (const [h, t] of defs) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11.2); doc.text(h, 14, dy);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10.2); doc.text(t, 14, dy + 6, { maxWidth: 186 });
    dy += 20;
  }

  if (signatureDataUrl) doc.addImage(signatureDataUrl, 'PNG', 100 - 44.4 / 2, 221.7, 44.4, 29.1, undefined, 'FAST');

  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  const bottom = [
    'Los resultados contenidos en el presente certificado se refieren exclusivamente a los equipos ó instrumentos sometidos a',
    'calibración ó medición, así como al momento y condiciones en que se realizaron las mediciones.',
  ];
  let by = 262;
  bottom.forEach((t) => { doc.text(t, 14, by); by += 4.6; });
}

// ── Generador principal ─────────────────────────────────────────────────────
export async function generateElectricalNoSacCertificatePdf({ session, instrument, blocks = [], results = [] }, opts = {}) {
  if (!session) throw new Error('session es requerida');
  if (!results.length) throw new Error('No hay resultados para certificar');

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const logo = await fetchImageAsDataUrl(opts.logoUrl || LOGO_URL);
  const signature = await fetchImageAsDataUrl(opts.signatureUrl || SIGNATURE_URL);

  const certNo = resolveCertificateNumber(
    opts.serviceOrderCode,
    session?.service_order_code,
    session?.certificate_number,
    instrument?.lab_id
  );

  // Preparar funciones aplicables (en orden) con sus resultados.
  const blockById = Object.fromEntries((blocks || []).map((b) => [b.id, b]));
  const byKey = {};
  for (const r of results) {
    const b = blockById[r.block_id];
    if (!b) continue;
    (byKey[b.block_key] = byKey[b.block_key] || []).push(r);
  }
  const functions = ELECTRICAL_FUNCTIONS
    .filter((f) => {
      const b = (blocks || []).find((x) => x.block_key === f.key);
      return b && b.applicable && (byKey[f.key] || []).length;
    })
    .map((f) => {
      const rs = (byKey[f.key] || []).slice().sort((a, b) => a.point_no - b.point_no);
      const byPoint = {};
      rs.forEach((r) => { byPoint[r.point_no] = r; });
      const maxPoint = rs.reduce((m, r) => Math.max(m, r.point_no), 0);
      return { key: f.key, label: f.label, unitLabel: f.unitLabel, results: rs, byPoint, maxPoint };
    });

  const determinations = functions.reduce((acc, f) => acc + f.results.length, 0);

  // Contenido (los encabezados/pies se dibujan al final con el total real).
  drawPage1(doc, { session, instrument, determinations });
  drawResultsSections(doc, { functions });
  drawFinalPage(doc, { session, signatureDataUrl: signature });

  const N = doc.getNumberOfPages();
  for (let p = 1; p <= N; p++) {
    doc.setPage(p);
    drawHeaderTemplate(doc, { logoDataUrl: logo, certificateNumber: certNo, page: p, pages: N, variant: p === 1 ? 'page1' : 'inner' });
    drawFooterTemplate(doc, p, N);
  }

  if (opts.returnBlob) {
    return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  }
  doc.save(opts.filename || `${certNo && certNo !== '-' ? certNo : 'R-DT-PC-ELEC'}.pdf`);
  return null;
}

